// Function index:
//   TIER_BLURB — what each plan actually buys, in player-facing terms.
//   renderBilling(host) — show the current plan, a Subscribe button per
//       subscription product (disabled for the current plan), and a Manage
//       button when subscribed. Dynamic text is set via textContent (no
//       innerHTML interpolation of server/model values).

import {
  getBillingEntitlements, listBillingProducts, startBillingCheckout, openBillingPortal,
  type BillingProduct,
} from '../../api.js';

// A plan_key on its own ("basic") tells a player nothing about what changes.
// The two real levers are the GM narration model and whether the paid
// background pipeline runs — continuity, lore, pacing, recall, the director
// (gm/post-response: free parties get narration + the NPC ledger only).
// Keys must match forge-pay/tier.ts::KNOWN_PLANS; an unlisted plan renders its
// raw key with no description rather than an invented one.
export const TIER_BLURB: Record<string, string> = {
  free: 'A free community GM model. The world remembers who you have met, but little else.',
  basic: 'Claude Sonnet 5 as your GM, plus the full memory pipeline — continuity checks, '
    + 'growing lore, pacing, semantic recall and a scene director.',
  pro: 'Claude Opus 4.8 as your GM — the strongest narrator available — plus the full '
    + 'memory pipeline: continuity checks, growing lore, pacing, semantic recall and a scene director.',
};

export async function renderBilling(host: HTMLElement): Promise<void> {
  host.textContent = 'Loading…';
  try {
    const [ent, products] = await Promise.all([getBillingEntitlements(), listBillingProducts()]);
    const plan = ent.subscription?.plan_key ?? 'free';
    host.textContent = '';

    host.appendChild(planSummary(plan));

    const list = document.createElement('div');
    list.className = 'party-list';
    host.appendChild(list);

    // One inline slot for action errors. A blocking alert() loses the message
    // the moment it is dismissed, and cannot be acted on.
    const errorSlot = document.createElement('div');
    errorSlot.className = 'usage-error bill-error';
    errorSlot.hidden = true;
    host.appendChild(errorSlot);
    const fail = (err: unknown): void => {
      errorSlot.textContent = (err as Error).message || 'Something went wrong.';
      errorSlot.hidden = false;
    };

    for (const p of products.filter((x: BillingProduct) => x.kind === 'subscription')) {
      list.appendChild(productRow(p, plan, fail));
    }

    if (ent.subscription) host.appendChild(manageButton(fail));
  } catch (err) {
    host.textContent = '';
    // 503 means the server has no FORGE_PAY_KEY: billing is switched off, not
    // broken. Reporting "could not load" sends a player — or an operator —
    // hunting a fault that does not exist.
    const notConfigured = (err as { status?: number }).status === 503;
    if (!notConfigured) console.error('billing-fetch error:', err);
    const box = document.createElement('div');
    box.className = notConfigured ? 'party-meta bill-off' : 'usage-error';
    box.textContent = notConfigured
      ? 'Subscriptions are not set up on this server yet — everyone is on the free plan.'
      : 'Could not load billing';
    host.appendChild(box);
  }
}

function planSummary(plan: string): HTMLElement {
  const wrap = document.createElement('div');
  const line = document.createElement('div');
  line.className = 'party-meta';
  line.textContent = `Current plan: ${plan}`;
  wrap.appendChild(line);
  const blurb = TIER_BLURB[plan];
  if (blurb) {
    const desc = document.createElement('div');
    desc.className = 'bill-blurb';
    desc.textContent = blurb;
    wrap.appendChild(desc);
  }
  return wrap;
}

function productRow(p: BillingProduct, plan: string, fail: (e: unknown) => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'bill-row';

  const text = document.createElement('div');
  text.className = 'bill-text';

  const label = document.createElement('div');
  label.className = 'bill-name';
  const price = p.unit_amount != null ? ` — ${(p.unit_amount / 100).toFixed(2)} ${p.currency.toUpperCase()}` : '';
  label.textContent = `${p.entitlement_key}${price}`;
  text.appendChild(label);

  const blurb = TIER_BLURB[p.entitlement_key];
  if (blurb) {
    const desc = document.createElement('div');
    desc.className = 'bill-blurb';
    desc.textContent = blurb;
    text.appendChild(desc);
  }
  row.appendChild(text);

  const isCurrent = p.entitlement_key === plan;
  const btn = document.createElement('button');
  btn.className = isCurrent ? 'secondary' : 'secondary bill-sub';
  btn.textContent = isCurrent ? 'Current' : 'Subscribe';
  btn.disabled = isCurrent;
  btn.dataset.priceId = p.price_id;
  btn.addEventListener('click', () => {
    btn.disabled = true;
    void startBillingCheckout(p.price_id)
      .then(({ url }) => { window.location.href = url; })
      // Re-enabled so a transient failure stays retryable rather than terminal.
      .catch((err) => { btn.disabled = false; fail(err); });
  });
  row.appendChild(btn);
  return row;
}

function manageButton(fail: (e: unknown) => void): HTMLElement {
  const manage = document.createElement('button');
  manage.className = 'secondary bill-manage';
  manage.textContent = 'Manage subscription';
  manage.addEventListener('click', () => {
    manage.disabled = true;
    void openBillingPortal()
      .then(({ url }) => { window.location.href = url; })
      .catch((err) => { manage.disabled = false; fail(err); });
  });
  return manage;
}
